import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Typography,
  Paper,
  Grid,
  Zoom,
  IconButton,
  Modal,
  Tooltip,
  Link,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";
import { Info } from "@mui/icons-material";
import QrCodeIcon from "@mui/icons-material/QrCode";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useDispatch, useSelector } from "react-redux";
import {
  createOrderFormUpdate,
  clearOrderFormUpdateUrl,
} from "../../../redux/features/adminEventsSlice";
import useBreakpoint from "../../../breakpointHook";
import TicketDetailsModalContents from "./TicketDetailsModalContents";
import TicketTypeName from "./TicketTypeName";
import QRCodeDialog from "./QRCodeDialog";
import { getPurchaserName } from "./utils";

const getOrderGroupKey = (ticket) =>
  ticket.order?.id ?? `no-order-${ticket.id}`;

const attendeeIdFromEmail = (email) =>
  typeof email === "string" ? email.trim().toLowerCase() : "";

export const TicketList = ({
  event,
  sessionId,
  tickets,
  eventCountByAttendeeId = {},
  handleCheckInClick,
  positiveButtonText,
  negativeButtonText,
  handleEditChange,
  checkIsPositive,
  onOrderIdClicked,
  onOrderIdFilterClick,
  handleTicketTypeNameClicked,
  groupByOrder,
  orderGroupCounts: orderGroupCountsProp,
  expandedHiddenOrderId,
  onToggleHiddenOrder,
  checkInSessionId,
  ticketsBeforeCheckInFilter,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const breakpoint = useBreakpoint();
  const [orderFormModalTicketId, setOrderFormModalTicketId] = useState(null);
  const [editingOrderFormKey, setEditingOrderFormKey] = useState(null);
  const qrCodeUrl = useSelector(
    (state) => state.adminEvents.orderFormUpdateUrl,
  );
  const creatingOrderFormUpdate = useSelector(
    (state) => state.adminEvents.creatingOrderFormUpdate,
  );

  const handleCreateOrderFormUpdate = (ticketId) => {
    if (creatingOrderFormUpdate) {
      return;
    }
    const { id: eventId } = event;
    dispatch(
      createOrderFormUpdate({
        eventId,
        ticketId,
        sessionId,
      }),
    );
  };

  const handleCloseQrCodeDialog = () => {
    dispatch(clearOrderFormUpdateUrl());
  };

  const { timezone } = event || {};

  const setEditing = (item) => {
    if (item) {
      handleEditChange(item);
    }
    setOrderFormModalTicketId(item);
  };

  const orderGroupCounts = useMemo(() => {
    if (orderGroupCountsProp) {
      return orderGroupCountsProp;
    }
    const counts = {};
    tickets.forEach((t) => {
      const key = getOrderGroupKey(t);
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [tickets, orderGroupCountsProp]);

  const visibleCountByOrder = useMemo(() => {
    const counts = {};
    tickets.forEach((t) => {
      const key = getOrderGroupKey(t);
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [tickets]);

  return (
    <Box display={"flex"} flexDirection={"column"} gap={"20px"}>
      <Modal
        open={Boolean(orderFormModalTicketId)}
        onClose={() => {
          setEditing(null);
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "100%", sm: 500, md: 600 },
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <TicketDetailsModalContents
            {...{
              event,
              ticket: tickets.find(({ id }) => id === orderFormModalTicketId),
              timezone: timezone,
              handleClose: () => {
                setEditing(null);
                setEditingOrderFormKey(null);
              },
              editingOrderFormKey,
              setEditingOrderFormKey,
              onOrderIdClicked,
              onOrderIdFilterClick,
            }}
          />
        </Box>
      </Modal>
      <QRCodeDialog
        open={Boolean(qrCodeUrl)}
        url={qrCodeUrl}
        onClose={handleCloseQrCodeDialog}
      />
      {tickets.map((ticket, index) => {
        const orderGroupKey = getOrderGroupKey(ticket);
        const prevOrderGroupKey =
          index > 0 ? getOrderGroupKey(tickets[index - 1]) : null;
        const showOrderHeader =
          groupByOrder && orderGroupKey !== prevOrderGroupKey;

        const {
          id,
          name,
          displayName,
          displayEmail,
          displayZipcode,
          displayLocationFromZipcode,
          checkIn,
          sessionCheckIns,
          ticketTypeKey,
          duplicateEmail,
          sessionId: ticketSessionId,
          sessionName,
        } = ticket;

        let checkedIn;
        if (checkIsPositive) {
          checkedIn = checkIsPositive(ticket);
        } else {
          // When checking in for a session, use that session's check-in state for the button
          const sessionKey = checkInSessionId ?? sessionId;
          checkedIn = sessionKey
            ? Boolean((sessionCheckIns || {})[sessionKey])
            : Boolean(checkIn);
        }

        let startIcon = null;
        switch (breakpoint.getBreakPointName()) {
          case "xs":
          case "sm":
            startIcon = null;
            break;
          case "md":
          case "lg":
          case "xl":
            startIcon = <CheckIcon />;
            break;
          default:
            startIcon = null;
            break;
        }

        const purchaserName = getPurchaserName(ticket);
        const orderId = ticket.order?.id;
        const hasOrderId = Boolean(orderId);
        const ticketCount = orderGroupCounts[orderGroupKey] || 1;
        const ticketCountText =
          ticketCount === 1 ? "1 ticket" : `${ticketCount} tickets`;
        const groupDisplayName = orderGroupKey.startsWith("no-order-")
          ? purchaserName || "No order"
          : purchaserName || "Unknown";
        const visibleCount = visibleCountByOrder[orderGroupKey] ?? 0;
        const hiddenCount = ticketCount - visibleCount;
        const hasHiddenByFilter =
          groupByOrder && hiddenCount > 0 && onToggleHiddenOrder;
        const isExpanded = expandedHiddenOrderId === orderGroupKey;

        const handleGroupHeaderClick = () => {
          if (hasOrderId && onOrderIdClicked) {
            onOrderIdClicked(orderId);
          }
        };

        const handleToggleHiddenClick = (e) => {
          e.stopPropagation();
          if (hasHiddenByFilter) {
            onToggleHiddenOrder(isExpanded ? null : orderGroupKey);
          }
        };

        return (
          <Box key={`ticket-${id}`}>
            {showOrderHeader && (
              <Box
                data-testid={`order-group-header-${orderGroupKey}`}
                sx={{
                  marginBottom: "12px",
                  marginTop: index > 0 ? "24px" : 0,
                }}
              >
                <Divider sx={{ marginBottom: "8px" }} />
                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
                  <Typography
                    variant="overline"
                    color={
                      hasOrderId && onOrderIdClicked ? "primary" : "primary.lightText"
                    }
                    component="div"
                    sx={{
                      display: "block",
                      ...(hasOrderId &&
                        onOrderIdClicked && {
                          cursor: "pointer",
                          "&:hover": { textDecoration: "underline" },
                        }),
                    }}
                    onClick={hasOrderId && onOrderIdClicked ? handleGroupHeaderClick : undefined}
                  >
                    {groupDisplayName} ({ticketCountText})
                  </Typography>
                  {hasHiddenByFilter && (
                    <>
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        component="span"
                        sx={{ display: "inline-block" }}
                      >
                        — {hiddenCount} hidden by filter
                      </Typography>
                      <Tooltip title={isExpanded ? "Hide hidden tickets" : "Show hidden tickets"}>
                        <IconButton
                          size="small"
                          onClick={handleToggleHiddenClick}
                          data-testid={`toggle-hidden-tickets-${orderGroupKey}`}
                          aria-label={isExpanded ? "Hide hidden tickets" : "Show hidden tickets"}
                        >
                          {isExpanded ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Box>
            )}
            <Paper
              sx={{ padding: "20px", position: "relative" }}
              elevation={5}
            >
            <Grid container>
              <Grid item xs={8} sm={8} md={4}>
                <Typography variant="h6">{displayName}</Typography>
                <Typography variant="subtitle2" color="primary.lightText">
                  {displayEmail}
                </Typography>
                <Typography variant="subtitle2" color="primary.lightText">
                  {displayLocationFromZipcode
                    ? `${displayLocationFromZipcode} ${displayZipcode || ""}`.trim()
                    : `Zipcode: ${displayZipcode}`}
                </Typography>
                {(() => {
                  const attendeeId = attendeeIdFromEmail(displayEmail);
                  const count = attendeeId
                    ? eventCountByAttendeeId[attendeeId]
                    : undefined;
                  if (attendeeId && count !== undefined) {
                    return (
                      <Typography
                        variant="subtitle2"
                        color="primary.lightText"
                      >
                        {count <= 1
                          ? "1st event"
                          : `Events attended: ${count}`}
                      </Typography>
                    );
                  }
                  return null;
                })()}
                <Box display={{ xs: "block", sm: "block", md: "none" }}>
                  <TicketTypeName
                    name={name}
                    handleTicketTypeNameClicked={
                      handleTicketTypeNameClicked
                        ? () => {
                            handleTicketTypeNameClicked(ticketTypeKey);
                          }
                        : null
                    }
                  />                    
                  {sessionName && ticketSessionId && (
                    <Typography
                      variant="subtitle2"
                      color="primary.lightText"
                      sx={{ marginTop: "4px" }}
                    >
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => {
                          navigate(
                            `/events/${event.id}/sessions/${ticketSessionId}`,
                          );
                        }}
                        sx={{
                          textDecoration: "underline",
                          cursor: "pointer",
                          color: "primary.main",
                        }}
                      >
                        {sessionName}
                      </Link>
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid
                item
                sm={5}
                display={{ xs: "none", sm: "none", md: "block" }}
              >
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  alignItems={"flex-start"}
                  justifyContent={"center"}
                  height={"100%"}
                >
                  <TicketTypeName
                    name={name}
                    handleTicketTypeNameClicked={
                      handleTicketTypeNameClicked
                        ? () => {
                            handleTicketTypeNameClicked(ticketTypeKey);
                          }
                        : null
                    }
                  />                   
                  {sessionName && ticketSessionId && (
                    <Typography
                      variant="subtitle2"
                      color="primary.lightText"
                      sx={{ marginTop: "4px" }}
                    >
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => {
                          navigate(
                            `/events/${event.id}/sessions/${ticketSessionId}`,
                          );
                        }}
                        sx={{
                          textDecoration: "underline",
                          cursor: "pointer",
                          color: "primary.main",
                        }}
                      >
                        {sessionName}
                      </Link>
                    </Typography>
                  )}                  
                </Box>
              </Grid>
              <Grid item xs={4} sm={4} md={3}>
                <Box
                  sx={{
                    padding: "0",
                    height: "100%",
                    display: "flex",
                    flexDirection: {
                      xs: "column",
                      sm: "column",
                      md: "row",
                    },
                    alignItems: "center",
                    justifyContent: {
                      xs: "center",
                      sm: "center",
                      md: "right",
                    },
                    alignSelf: {
                      xs: "center",
                      sm: "center",
                      md: "stretch",
                    },
                    gap: {
                      xs: "20px",
                      sm: "20px",
                      md: "10px",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                      order: { xs: 2, sm: 2, md: 1 },
                    }}
                  >
                    <Tooltip title="Details">
                      <IconButton
                        onClick={() => {
                          setEditing(id);
                        }}
                      >
                        <Info color="primary" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Show QR Code for Customer Info Update">
                      <IconButton
                        onClick={() => handleCreateOrderFormUpdate(id)}
                        disabled={creatingOrderFormUpdate}
                      >
                        <QrCodeIcon
                          color={duplicateEmail ? "warning" : "primary"}
                        />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box
                    sx={{
                      width: "150px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      order: { xs: 1, sm: 1, md: 2 },
                    }}
                  >
                    <Zoom in={checkedIn}>
                      <Button
                        variant="contained"
                        color="success"
                        size={"small"}
                        sx={{
                          width: { sm: "75px", md: "150px" },
                          position: "absolute",
                        }}
                        startIcon={startIcon}
                        onClick={() => {
                          handleCheckInClick(ticket, false);
                        }}
                      >
                        {positiveButtonText || "Checked In"}
                      </Button>
                    </Zoom>

                    <Zoom in={!checkedIn}>
                      <Button
                        variant="outlined"
                        sx={{
                          width: { sm: "75px", md: "150px" },
                          position: "absolute",
                        }}
                        size={"small"}
                        onClick={() => {
                          handleCheckInClick(ticket, true);
                        }}
                      >
                        {negativeButtonText || "Check In"}
                      </Button>
                    </Zoom>
                  </Box>
                </Box>
              </Grid>
            </Grid>
            <Box></Box>
          </Paper>
          </Box>
        );
      })}
    </Box>
  );
};
